import { extractObjectId } from '@lib/utils/url';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useLocation } from 'react-router';

/**
 * Returns the single library ID most relevant to the user's current context:
 * 1. If on a /library/{id} page, returns that library's ID.
 * 2. Otherwise, if exactly one library is selected for search, returns that ID.
 * 3. Otherwise returns null (multiple or no libraries selected).
 *
 * NOTE: StarZero Agents does not currently support dealing with multiple
 * libraries at the same time. If that changes, this hook should be extended
 * to return an array of active library IDs as well.
 *
 * IMPORTANT: No useMemo here — selectedLibraryIdsToSearch is a MobX ObservableSet
 * with a stable reference. useMemo would never re-compute when the set contents
 * change. The consuming components must be wrapped in observer() so MobX tracks
 * the read during render.
 */
export function useActiveLibraryId(): string | null {
  const currentPath = useLocation().pathname;
  const libraryManager = useLibraryManager();

  // If viewing a specific library, that's the active one
  const libraryMatch = currentPath.match(/^\/library\/([^/]+)/);
  if (libraryMatch) {
    return extractObjectId(libraryMatch[1]);
  }

  // Fall back to the single selected search library
  const selected = libraryManager.selectedLibraryIdsToSearch;
  if (selected.size === 1) {
    return Array.from(selected)[0];
  }

  return null;
}
